import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Page, Spinner } from "@shopify/polaris";
import { useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import useResizeObserver from "use-resize-observer";
import useIsPhone from "features/files/use-is-phone";

import { RootState } from "configureStore";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxOptions,
} from "components/modalx/modalx.slice";
import {
  fetchLeaveRequestById,
  fetchLeaveRequests,
  fetchLeaveSummaries,
} from "services/leave-requests.service";
import { serializedLeaveToRecord } from "./leaveRecordMappers";
import { isSameDay, eachDayOfInterval, startOfDay } from "date-fns";
import {
  LEAVE_CALENDAR_CELL_WIDTH,
  LEAVE_CALENDAR_MIN_CELL_WIDTH,
  leaveCalendarLeaveTypeI18nKey,
} from "./leaveCalendar.constants";
import {
  dayKey,
  leaveCalendarDateLocale,
  monthDayInterval,
  yearDayInterval,
} from "./leaveCalendarDates";
import {
  fetchHolidaySettings,
  fetchCompanyHolidays,
} from "../company-holidays/companyHolidays.service";
import type { CompanySettings, CompanyHoliday } from "./leaveCalendar.types";
import { parseISO, format, isValid } from "date-fns";
import { fetch as fetchUsers } from "../../services/user-select.service";
import { LeaveCalendarToolbar } from "./LeaveCalendarToolbar";
import { LeaveCalendarUserPanel } from "./LeaveCalendarUserPanel";
import type { UserMonthSummary } from "./LeaveCalendarUserPanel";
import { LeaveCalendarDateHeader } from "./LeaveCalendarDateHeader";
import { LeaveCalendarLegend, LeaveCalendarLegendToggle } from "./LeaveCalendarLegend";
import { LeaveCalendarVirtualBody } from "./LeaveCalendarVirtualBody";
import { useLeaveDragSelect } from "./useLeaveDragSelect";
import type { LeaveRecord, PublicHoliday, CalendarUser } from "./leaveCalendar.types";
import { useLeaveCalendarSocket } from "./useLeaveCalendarSocket";

/** A touch screen cannot hover, so the tooltip that names a holiday never shows there. */
function hasNoHover(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia("(hover: none)").matches;
}

export default function LeaveCalendarPage() {
  const { companyId } = useParams<{ companyId: string }>();
  useLeaveCalendarSocket(companyId);
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkLeaveId = searchParams.get("leave");
  const { t, i18n } = useTranslation();
  const isPhone = useIsPhone();
  const dispatch = useDispatch();
  const refreshNonce = useSelector(
    (s: RootState) => s.leaveCalendar.refreshNonce
  );
  const leaveModalOpen = useSelector(
    (s: RootState) =>
      s.modalxReducer.isOpen &&
      s.modalxReducer.component === "LeaveCalendarLeaveModal"
  );
  const prevLeaveModalOpenRef = useRef(false);
  const sessionUserId = useSelector(
    (s: RootState) => s.authReducer.role?.user || ""
  );
  const [users, setUsers] = useState<CalendarUser[]>([]);
  const [leaves, setLeaves] = useState<LeaveRecord[]>([]);
  const [summaries, setSummaries] = useState<Record<string, UserMonthSummary>>({});
  const [holidayCount, setHolidayCount] = useState(0);
  const [weekendHolidayCount, setWeekendHolidayCount] = useState(0);
  const [usersLoading, setUsersLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  // The leaves, the holidays and the working-day settings that belong to the days shown. They are loading
  // again after every month or year change; the «Add leave» button waits for all of them.
  const [isLeavesLoading, setIsLeavesLoading] = useState(true);
  const [isHolidaysLoading, setIsHolidaysLoading] = useState(true);
  const [isSettingsLoading, setIsSettingsLoading] = useState(true);
  const [highlightedLeaveId, setHighlightedLeaveId] = useState<string | null>(
    null
  );
  /** The tapped holiday's date and name, shown above the grid for a few seconds. */
  const [holidayHint, setHolidayHint] = useState<{ date: Date; name: string } | null>(null);
  const deepLinkOpenedRef = useRef<string | null>(null);
  const highlightClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [monthIndex, setMonthIndex] = useState(now.getMonth());
  const [viewMode, setViewMode] = useState<"month" | "year">("month");
  const { ref: listAreaRef, height: listAreaHeight = 420 } =
    useResizeObserver<HTMLDivElement>();

  const [settings, setSettings] = useState<CompanySettings>({
    saturdayOff: true,
    sundayOff: true,
  });
  const [companyHolidaysData, setCompanyHolidaysData] = useState<CompanyHoliday[]>([]);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setIsSettingsLoading(true);
    const loadSettings = async () => {
      try {
        const s = await fetchHolidaySettings(companyId);
        if (!cancelled) setSettings(s);
      } catch (e) {
        console.error("Failed to load holiday settings", e);
      } finally {
        if (!cancelled) setIsSettingsLoading(false);
      }
    };
    loadSettings();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setIsHolidaysLoading(true);
    const loadHolidays = async () => {
      try {
        const h = await fetchCompanyHolidays(companyId, year);
        if (!cancelled) setCompanyHolidaysData(h);
      } catch (e) {
        console.error("Failed to load holidays", e);
      } finally {
        if (!cancelled) setIsHolidaysLoading(false);
      }
    };
    loadHolidays();
    return () => {
      cancelled = true;
    };
  }, [companyId, year]);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    const loadUsers = async () => {
      setUsersLoading(true);
      try {
        const res = await fetchUsers(companyId, {}, false, false);
        if (!cancelled) {
          setUsers(res.usersCollection || []);
        }
      } catch (e) {
        console.error("Failed to load users", e);
      } finally {
        if (!cancelled) {
          setUsersLoading(false);
        }
      }
    };
    loadUsers();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const holidaysInView = useMemo(() => {
    const list: PublicHoliday[] = [];
    companyHolidaysData
      .filter((ch) => ch.isActive)
      .forEach((ch) => {
        try {
          const interval = eachDayOfInterval({
            start: parseISO(ch.startDate),
            end: parseISO(ch.endDate),
          });
          interval.forEach((d: Date) => {
            list.push({
              date: format(d, "yyyy-MM-dd"),
              nameEn: ch.title,
              nameLt: ch.title,
            });
          });
        } catch (e) {}
      });
    return list;
  }, [companyHolidaysData]);

  const { saturdayOff, sundayOff } = settings;

  const { start: dateStart, end: dateEnd, days } = useMemo(
    () =>
      viewMode === "year"
        ? yearDayInterval(year)
        : monthDayInterval(year, monthIndex),
    [year, monthIndex, viewMode]
  );

  const resolveLeaveTypeLabel = useCallback(
    (code: string) =>
      t(leaveCalendarLeaveTypeI18nKey(code), { defaultValue: code }),
    [t]
  );

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setIsLeavesLoading(true);
    const fromStr = format(dateStart, "yyyy-MM-dd");
    const toStr = format(dateEnd, "yyyy-MM-dd");
    (async () => {
      try {
        const [reqRes, summaryByUser] = await Promise.all([
          fetchLeaveRequests(companyId, {
            from: fromStr,
            to: toStr,
          }),
          fetchLeaveSummaries(companyId, {
            from: fromStr,
            to: toStr,
          }),
        ]);
        if (cancelled) return;
        setLeaves(reqRes.leaveRequests.map(serializedLeaveToRecord));
        setSummaries(() => {
          const base: Record<string, UserMonthSummary> = {};
          users.forEach((u) => {
            base[u.id] = { leave: 0, remoteWork: 0 };
          });
          Object.entries(summaryByUser).forEach(([id, summary]) => {
            base[id] = {
              leave: summary.leave ?? 0,
              remoteWork: summary.remoteWork ?? 0,
            };
          });
          return base;
        });
      } catch (e) {
        console.error("Failed to load leave calendar data", e);
      } finally {
        if (!cancelled) {
          setInitialLoading(false);
          setIsLeavesLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, dateStart, dateEnd, refreshNonce, users]);

  useEffect(() => {
    const fromStr = format(dateStart, "yyyy-MM-dd");
    const toStr = format(dateEnd, "yyyy-MM-dd");
    const holidaySet = new Set(
      holidaysInView
        .map((h) => h.date)
        .filter((d) => d >= fromStr && d <= toStr)
    );
    setHolidayCount(holidaySet.size);

    let weekendCount = 0;
    for (const d of days) {
      const key = format(d, "yyyy-MM-dd");
      const day = d.getDay();
      const weekend = (day === 0 && sundayOff) || (day === 6 && saturdayOff);
      if (weekend && !holidaySet.has(key)) {
        weekendCount += 1;
      }
    }
    setWeekendHolidayCount(weekendCount);
  }, [days, holidaysInView, dateStart, dateEnd, saturdayOff, sundayOff]);

  const lang = (i18n as any).language as string | undefined;

  const locale = useMemo(
    () => leaveCalendarDateLocale(lang),
    [lang]
  );


  const hScrollRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<any>(null);
  const leftScrollRef = useRef<HTMLDivElement>(null);
  const syncSource = useRef<"list" | "left" | null>(null);
  /** Matches vertical scrollbar width on the grid list so header + columns stay aligned (no inner horizontal bar). */
  const [listVerticalScrollbarW, setListVerticalScrollbarW] = useState(0);
  /** Timeline viewport width — day columns grow to fill when there is room. */
  const [timelineViewportW, setTimelineViewportW] = useState(0);

  useEffect(() => {
    const el = hScrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width ?? el.clientWidth;
      setTimelineViewportW(Math.floor(w));
    });
    ro.observe(el);
    setTimelineViewportW(el.clientWidth);
    return () => ro.disconnect();
  }, [initialLoading]);

  const cellWidth = useMemo(() => {
    const n = Math.max(1, days.length);
    if (timelineViewportW <= 0) return LEAVE_CALENDAR_CELL_WIDTH;
    const usable = Math.max(0, timelineViewportW - listVerticalScrollbarW);
    const fitted = Math.floor(usable / n);
    // Grow columns to fill the viewport in month view; year view stays at min and scrolls.
    // A phone keeps the full-size day cell and scrolls sideways instead of squeezing a finger-sized cell.
    const minWidth = isPhone ? LEAVE_CALENDAR_CELL_WIDTH : LEAVE_CALENDAR_MIN_CELL_WIDTH;
    return Math.max(minWidth, fitted);
  }, [days.length, timelineViewportW, listVerticalScrollbarW, isPhone]);

  const timelineWidth = days.length * cellWidth;
  const timelineOuterWidth = timelineWidth + listVerticalScrollbarW;

  const isOffDay = useCallback(
    (date: Date) => {
      const day = date.getDay(); // Local day
      if (day === 0 && settings.sundayOff) return true;
      if (day === 6 && settings.saturdayOff) return true;
      const dateStr = format(date, "yyyy-MM-dd");
      return holidaysInView.some((h: any) => h.date === dateStr);
    },
    [settings.sundayOff, settings.saturdayOff, holidaysInView]
  );

  const showCreateModal = useCallback(
    (startDate: string, endDate: string) => {
      dispatch(setModalxComponent("LeaveCalendarLeaveModal"));
      dispatch(
        setModalxOptions({
          title: t("leave_calendar_modal_create_title"),
        })
      );
      dispatch(
        setModalxComponentProps({
          mode: "create",
          startDate,
          endDate,
          companyId,
          users,
          settings,
          holidays: holidaysInView,
          allLeaves: leaves,
        })
      );
    },
    [dispatch, t, companyId, users, settings, holidaysInView, leaves]
  );

  const openCreateModal = useCallback(
    (_userId: string, startIdx: number, endIdx: number) => {
      // Only allow creating leaves for yourself
      if (_userId !== sessionUserId) return;

      const a = Math.min(startIdx, endIdx);
      const b = Math.max(startIdx, endIdx);

      // Block if selected start date is in the past
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (days[a] < today) {
        toast.error(t("leave_calendar_past_date_error", { defaultValue: "Start date cannot be in the past" }));
        return;
      }

      if (isOffDay(days[a]) || isOffDay(days[b])) {
        toast.error(t("leave_calendar_off_day_error_toast", { defaultValue: "You cannot add a leave request on a weekend or holiday" }));
        return;
      }

      showCreateModal(dayKey(days[a]), dayKey(days[b]));
    },
    [days, t, sessionUserId, isOffDay, showCreateModal]
  );

  // The «Add leave» button opens the dialog on the first working day from today among the days shown, because the
  // dialog checks dates against the holidays and leaves loaded for these days. No such day (a month in the past,
  // or only days off left in it) or data still loading: the button is disabled.
  const addLeaveDay = useMemo(() => {
    const today = startOfDay(new Date());
    return days.find((day) => day >= today && !isOffDay(day)) ?? null;
  }, [days, isOffDay]);
  const isCalendarDataLoading =
    usersLoading || isLeavesLoading || isHolidaysLoading || isSettingsLoading;
  const canAddLeave = addLeaveDay !== null && !isCalendarDataLoading;

  const openCreateModalOnAddLeaveDay = useCallback(() => {
    if (addLeaveDay) showCreateModal(dayKey(addLeaveDay), dayKey(addLeaveDay));
  }, [addLeaveDay, showCreateModal]);

  const { dragPreview, onRowPointerDown, isWaitingForLastDay, cancelTapSelection } =
    useLeaveDragSelect({
      cellWidth,
      dayCount: days.length,
      hScrollRef,
      onCommit: openCreateModal,
      touchUserId: sessionUserId,
    });

  // A tapped first day means nothing once the calendar shows other days.
  useEffect(() => {
    cancelTapSelection();
  }, [days, cancelTapSelection]);

  useEffect(() => {
    if (!holidayHint) return undefined;
    const timer = setTimeout(() => setHolidayHint(null), 6000);
    return () => clearTimeout(timer);
  }, [holidayHint]);

  const openViewModal = useCallback(
    (leave: LeaveRecord) => {
      dispatch(setModalxComponent("LeaveCalendarLeaveModal"));
      dispatch(
        setModalxOptions({
          title: t("leave_calendar_modal_view_title"),
        })
      );
      dispatch(
        setModalxComponentProps({
          mode: "view",
          leave,
          companyId,
          users,
          settings,
          holidays: holidaysInView,
          allLeaves: leaves,
        })
      );
    },
    [
      dispatch,
      t,
      companyId,
      users,
      settings,
      holidaysInView,
      leaves,
    ]
  );

  const clearDeepLinkParam = useCallback(() => {
    setSearchParams(
      (params) => {
        params.delete("leave");
        return params;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const emphasizeLeaveOnCalendar = useCallback((record: LeaveRecord) => {
    setHighlightedLeaveId(record.id);
    if (highlightClearTimerRef.current) {
      clearTimeout(highlightClearTimerRef.current);
    }
    highlightClearTimerRef.current = setTimeout(() => {
      setHighlightedLeaveId(null);
      highlightClearTimerRef.current = null;
    }, 10000);

    const userId = record.userId;
    const userIdx = users.findIndex((u) => u.id === userId);
    if (userIdx >= 0) {
      requestAnimationFrame(() => {
        listRef.current?.scrollToItem(userIdx, "center");
      });
    }

    const start = parseISO(record.startDate);
    if (isValid(start) && hScrollRef.current) {
      const dayIdx = days.findIndex((d) => isSameDay(d, start));
      if (dayIdx >= 0) {
        const el = hScrollRef.current;
        const target =
          dayIdx * cellWidth - el.clientWidth / 2;
        el.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
      }
    }
  }, [users, days, cellWidth]);

  useEffect(() => {
    if (!deepLinkLeaveId) {
      deepLinkOpenedRef.current = null;
    }
  }, [deepLinkLeaveId]);

  useEffect(() => {
    const wasOpen = prevLeaveModalOpenRef.current;
    prevLeaveModalOpenRef.current = leaveModalOpen;
    if (wasOpen && !leaveModalOpen && deepLinkLeaveId) {
      clearDeepLinkParam();
      deepLinkOpenedRef.current = null;
    }
  }, [leaveModalOpen, deepLinkLeaveId, clearDeepLinkParam]);

  useEffect(() => {
    return () => {
      if (highlightClearTimerRef.current) {
        clearTimeout(highlightClearTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!highlightedLeaveId || initialLoading) return;
    const record = leaves.find((l) => l.id === highlightedLeaveId);
    if (!record) return;
    const userIdx = users.findIndex((u) => u.id === record.userId);
    if (userIdx >= 0) {
      requestAnimationFrame(() => {
        listRef.current?.scrollToItem(userIdx, "center");
      });
    }
  }, [leaves, highlightedLeaveId, users, initialLoading]);

  useEffect(() => {
    if (!companyId || !deepLinkLeaveId || initialLoading || usersLoading) return;
    if (deepLinkOpenedRef.current === deepLinkLeaveId) return;

    let cancelled = false;

    (async () => {
      let record = leaves.find((l) => l.id === deepLinkLeaveId);

      if (!record) {
        try {
          const { leaveRequest } = await fetchLeaveRequestById(
            companyId,
            deepLinkLeaveId
          );
          record = serializedLeaveToRecord(leaveRequest);
        } catch (e) {
          console.error("Failed to open leave from link", e);
          if (!cancelled) {
            toast.error(
              t("leave_calendar_deep_link_not_found", {
                defaultValue: "Could not open this leave on the calendar.",
              })
            );
            clearDeepLinkParam();
          }
          return;
        }
      }

      if (!record || cancelled) return;

      const start = parseISO(record.startDate);
      if (isValid(start)) {
        const targetYear = start.getFullYear();
        const targetMonth = start.getMonth();
        if (year !== targetYear || monthIndex !== targetMonth) {
          setYear(targetYear);
          setMonthIndex(targetMonth);
          setViewMode("month");
          return;
        }
      }

      deepLinkOpenedRef.current = deepLinkLeaveId;
      emphasizeLeaveOnCalendar(record);
      openViewModal(record);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    companyId,
    deepLinkLeaveId,
    initialLoading,
    usersLoading,
    leaves,
    year,
    monthIndex,
    openViewModal,
    clearDeepLinkParam,
    emphasizeLeaveOnCalendar,
    t,
  ]);

  const handleListScroll = useCallback((scrollOffset: number) => {
    if (syncSource.current === "left") return;
    syncSource.current = "list";
    const el = leftScrollRef.current;
    if (el && Math.abs(el.scrollTop - scrollOffset) > 1) {
      el.scrollTop = scrollOffset;
    }
    requestAnimationFrame(() => {
      syncSource.current = null;
    });
  }, []);

  const handleLeftScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (syncSource.current === "list") return;
      syncSource.current = "left";
      const top = e.currentTarget.scrollTop;
      listRef.current?.scrollTo(top);
      requestAnimationFrame(() => {
        syncSource.current = null;
      });
    },
    []
  );

  const holidayName = useCallback(
    (h: PublicHoliday) =>
      lang === "lt" ? h.nameLt : h.nameEn,
    [lang]
  );

  const prevMonthIndex = useRef(monthIndex);
  const scrollTodayAfterNav = useRef(true); // Jump to today on original load

  // Jump to specific month or today
  useEffect(() => {
    if (!hScrollRef.current) return;
    const el = hScrollRef.current;

    // First handle Jump to Today flow
    if (scrollTodayAfterNav.current) {
      scrollTodayAfterNav.current = false;
      const t0 = new Date();
      if (t0.getFullYear() === year) {
        if (viewMode === "month" && t0.getMonth() !== monthIndex) return;
        const idx = days.findIndex((d) => isSameDay(d, t0));
        if (idx >= 0) {
          const target = idx * cellWidth - el.clientWidth / 2;
          el.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
          return;
        }
      }
    }

    // Then handle explicit Month drop-down changes
    if (monthIndex !== prevMonthIndex.current) {
      prevMonthIndex.current = monthIndex;
      if (viewMode === "year") {
        const targetDate = new Date(year, monthIndex, 1);
        const idx = days.findIndex((d) => isSameDay(d, targetDate));
        if (idx >= 0) {
          const target = idx * cellWidth - 24; // slightly offset from left edge
          el.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
        }
      } else {
        el.scrollTo({ left: 0 }); // reset scroll in month view
      }
    }
  }, [year, monthIndex, days, viewMode, cellWidth]);

  const jumpToToday = useCallback(() => {
    const t0 = new Date();
    const y = t0.getFullYear();
    const m = t0.getMonth();
    
    if (y === year && (viewMode === "year" || m === monthIndex)) {
      const idx = days.findIndex((d) => isSameDay(d, t0));
      if (idx >= 0 && hScrollRef.current) {
        const el = hScrollRef.current;
        const target = idx * cellWidth - el.clientWidth / 2;
        el.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
      }
    } else {
      scrollTodayAfterNav.current = true;
      setYear(y);
      if (viewMode === "month") setMonthIndex(m);
    }
  }, [year, monthIndex, viewMode, days, cellWidth]);


  return (
    <Page fullWidth title={t("team_calendar")}>
      {/* On a phone the box also leaves room for the bottom bar, so the grid is not pushed under it. */}
      <div className="flex h-[calc(100dvh-7.5rem)] min-h-[420px] w-full flex-col overflow-hidden max-md:h-[calc(100dvh-8rem-var(--phone-bar-height,0px))] max-md:min-h-[360px]">
        <div className="sticky top-0 z-20 shrink-0 bg-[#f6f6f7] pb-2">
          <LeaveCalendarToolbar
            year={year}
            monthIndex={monthIndex}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onYearChange={setYear}
            onMonthChange={setMonthIndex}
            onJumpToday={jumpToToday}
            onAddLeave={openCreateModalOnAddLeaveDay}
            canAddLeave={canAddLeave}
          />
          {isWaitingForLastDay && (
            <div role="status" className="mb-2 flex items-center justify-between gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-1 text-sm text-slate-700">
              {t("leave_calendar_tap_last_day")}
              <button type="button" onClick={cancelTapSelection} className="min-h-11 px-3 font-medium text-indigo-700">
                {t("cancel")}
              </button>
            </div>
          )}
          {holidayHint && (
            <div role="status" className="mb-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-slate-700">
              {format(holidayHint.date, "PP", { locale })} · {holidayHint.name}
            </div>
          )}
        </div>

        <div className="flex min-h-0 flex-1 flex-row overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
          {initialLoading ? (
            <div className="flex flex-1 items-center justify-center bg-slate-50/30">
              <Spinner size="large" accessibilityLabel={t("loading")} />
            </div>
          ) : (
            <>
              <LeaveCalendarUserPanel
                users={users}
                summaries={summaries}
                holidayCount={holidayCount}
                weekendHolidayCount={weekendHolidayCount}
                scrollRef={leftScrollRef}
                onScroll={handleLeftScroll}
              />

              {/* One horizontal scroller: date header + grid stay pixel-aligned */}
              <div
                ref={hScrollRef}
                className="leave-calendar-main-scroll no-scrollbar flex min-h-0 min-w-0 flex-1 flex-col overflow-x-auto overflow-y-hidden"
              >
                <div
                  className="shrink-0"
                  style={{ width: timelineOuterWidth }}
                >
                  <LeaveCalendarDateHeader
                    days={days}
                    locale={locale}
                    holidays={holidaysInView}
                    holidayName={holidayName}
                    saturdayOff={saturdayOff}
                    sundayOff={sundayOff}
                    rightGutterPx={listVerticalScrollbarW}
                    cellWidth={cellWidth}
                    scrollContainerRef={hScrollRef}
                    onHolidayTap={
                      hasNoHover() ? (date, name) => setHolidayHint({ date, name }) : undefined
                    }
                  />
                </div>
                <div
                  ref={listAreaRef}
                  className="min-h-0 flex-1 overflow-hidden"
                  style={{ width: timelineOuterWidth }}
                >
                  <LeaveCalendarVirtualBody
                    height={listAreaHeight}
                    width={timelineWidth}
                    listOuterWidth={timelineOuterWidth}
                    cellWidth={cellWidth}
                    users={users}
                    days={days}
                    monthStart={dateStart}
                    monthEnd={dateEnd}
                    leaves={leaves}
                    holidays={holidaysInView}
                    listRef={listRef}
                    dragPreview={dragPreview}
                    onRowPointerDown={onRowPointerDown}
                    onLeaveClick={openViewModal}
                    resolveLeaveTypeLabel={resolveLeaveTypeLabel}
                    onScroll={handleListScroll}
                    onVerticalScrollbarWidthChange={setListVerticalScrollbarW}
                    saturdayOff={saturdayOff}
                    sundayOff={sundayOff}
                    highlightedLeaveId={highlightedLeaveId}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        <div className="mt-3 shrink-0">
          {isPhone ? <LeaveCalendarLegendToggle /> : <LeaveCalendarLegend />}
        </div>
      </div>
    </Page>
  );
}
